/**
 * audio_mute.ts — ONE switch that silences every game sound (2026-09-08: "the
 * sounds and music from the game are bleeding in when the intro video is
 * playing — mute everything until the video is skipped or ends").
 *
 * The client has several independent audio owners (the sfx bus, the music
 * and ambience modules, the engine AudioManager behind the audio zones, the
 * world-script tones); none shares a context. Each owner hangs a MUTE STAGE
 * on its output (`attachMuteStage`: master → mute → destination) or
 * registers an applier (`registerAudioMute`), and `setGameAudioMuted` drives
 * them all. An owner that comes up while muted is muted at once (the sfx
 * context is born on the first pointer gesture — which may be the click that
 * began the intro). globalThis-backed: vite's dev-graph duplication must
 * never split the switch from a late-registered owner.
 */
export const MUTE_RAMP_S = 0.05;

interface Registry { muted: boolean; appliers: Map<string, (muted: boolean) => void>; seq: number }
const store = globalThis as unknown as { __atlasAudioMute?: Registry };
const reg = (): Registry => (store.__atlasAudioMute ??= { muted: false, appliers: new Map(), seq: 0 });

export function isGameAudioMuted(): boolean { return reg().muted; }

/** Register an applier; it is called with the CURRENT state at once. Returns the unregister. */
export function registerAudioMute(name: string, apply: (muted: boolean) => void): () => void {
  const r = reg();
  const key = `${name}#${++r.seq}`;
  r.appliers.set(key, apply);
  try { apply(r.muted); } catch { /* an owner mid-teardown */ }
  return () => { r.appliers.delete(key); };
}

export function setGameAudioMuted(muted: boolean): void {
  const r = reg();
  if (r.muted === muted) return;
  r.muted = muted;
  for (const apply of r.appliers.values()) { try { apply(muted); } catch { /* an owner mid-teardown */ } }
}

/** Number of live appliers (probe diag). */
export function audioMuteOwners(): number { return reg().appliers.size; }

/** Hang a mute stage between an owner's master gain and its destination:
 *  master → mute(1|0) → destination. The stage follows the switch with a
 *  short ramp; returns the stage (the owner may keep it). */
export function attachMuteStage(ctx: AudioContext, master: AudioNode, name: string): GainNode {
  const stage = ctx.createGain();
  stage.gain.value = isGameAudioMuted() ? 0 : 1;
  master.connect(stage);
  stage.connect(ctx.destination);
  registerAudioMute(name, (muted) => {
    try {
      const t = ctx.currentTime;
      stage.gain.cancelScheduledValues(t);
      stage.gain.setValueAtTime(stage.gain.value, t);
      stage.gain.linearRampToValueAtTime(muted ? 0 : 1, t + MUTE_RAMP_S);
    } catch { stage.gain.value = muted ? 0 : 1; }
  });
  return stage;
}
